import axios from 'axios';

// The mobile app (mobile/lib/main.dart) is a WebView pointed straight at
// https://ikonetsolutions.com, so window.location.origin already resolves
// correctly there — no separate native-runtime base URL needed.
const getBaseURL = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};

const API_URL = getBaseURL();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to format API error messages
export function formatApiError(error) {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Tickets API
export const ticketsApi = {
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
  sendMessage: (id, data) => api.post(`/tickets/${id}/messages`, data)
};

// Technicians API
export const techniciansApi = {
  getAll: () => api.get('/technicians'),
  create: (data) => api.post('/technicians', data),
  update: (id, data) => api.put(`/technicians/${id}`, data),
  delete: (id) => api.delete(`/technicians/${id}`),
  updateStatus: (id, status) => api.put(`/technicians/${id}/status`, null, { params: { status } }),
  updateProfile: (data) => api.put('/technicians/profile', data)
};

// Calls API
export const callsApi = {
  getAll: (params) => api.get('/calls', { params }),
  getById: (id) => api.get(`/calls/${id}`)
};

// AI API
export const aiApi = {
  chat: (message, ticketId, context) => api.post('/ai/chat', { message, ticket_id: ticketId, context }),
  transcribe: (formData) => api.post('/ai/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Assets API
export const assetsApi = {
  getAll: (params) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`)
};

// Knowledge Base API
export const kbApi = {
  getAll: (params) => api.get('/kb', { params }),
  getById: (id) => api.get(`/kb/${id}`),
  create: (data) => api.post('/kb', data),
  update: (id, data) => api.put(`/kb/${id}`, data),
  delete: (id) => api.delete(`/kb/${id}`)
};

// Stats API
export const statsApi = {
  getDashboard: () => api.get('/stats/dashboard')
};

// Billing / license API
export const billingApi = {
  getLicense: () => api.get('/company/license'),
  validateIAP: (data) => api.post('/payments/validate-iap', data)
};

export default api;
